/**
 * Convert Markdown to PPTX using marked library
 *
 * Uses the same marked → HTML → DOM pattern as markdownToDocx and pdfRenderer
 * for consistent handling of all markdown features (ordered lists, links, formatting, etc.)
 */

import PptxGenJS from "pptxgenjs";
import { marked } from "marked";

export interface PptxConversionOptions {
  title?: string;
  author?: string;
  subject?: string;
  fontFamily?: string; // Body text font (from brand or user preference)
  codeFontFamily?: string; // Code blocks font (default: "Courier New")
}

/**
 * Options for adding markdown content to an existing presentation
 */
export interface AddSlidesToPresentationOptions {
  title?: string; // Title for the first slide (creates title slide if provided)
  fontFamily?: string; // Body text font (from brand or user preference)
  codeFontFamily?: string; // Code blocks font (default: "Courier New")
  skipTitleSlide?: boolean; // If true, skips creating a title slide even if title is provided
}

// ============================================================================
// SLIDE LAYOUT CONSTANTS
// PowerPoint 16:9 layout = 10" wide x 5.625" tall
// ============================================================================
const SLIDE_WIDTH = 10; // inches
const SLIDE_HEIGHT = 5.625; // inches
const SLIDE_MARGIN_X = 0.75; // Left/right margin (increased for professional look)
const SLIDE_MARGIN_TOP = 0.5; // Top margin for title
const SLIDE_MARGIN_BOTTOM = 0.5; // Bottom margin
const CONTENT_WIDTH = SLIDE_WIDTH - 2 * SLIDE_MARGIN_X; // 8.5 inches usable

// Content positioning
const TITLE_HEIGHT = 0.7; // Height reserved for slide title
const TITLE_CONTENT_GAP = 0.4; // Gap between title and content (increased)
const CONTENT_START_Y = SLIDE_MARGIN_TOP + TITLE_HEIGHT + TITLE_CONTENT_GAP; // ~1.6" - where content starts
const MAX_CONTENT_Y = SLIDE_HEIGHT - SLIDE_MARGIN_BOTTOM; // ~5.1" - max Y before overflow

// Text layout calculations
const CHARS_PER_LINE = 80; // Characters per line at body font size
const LINE_HEIGHT = 0.35; // Line height in inches for body text (increased for readability)
const PARAGRAPH_SPACING = 0.4; // Space between paragraphs (increased)

// ============================================================================
// FONT SIZES FOR PRESENTATION DISPLAY
// Optimised for readability in presentation mode
// ============================================================================
const TITLE_FONT_SIZE = 32; // Slide title (h1) - slightly smaller for cleaner look
const SUBTITLE_FONT_SIZE = 26; // h2
const HEADING_FONT_SIZE = 22; // h3-h6
const BODY_FONT_SIZE = 16; // Body text - balanced for readability
const CODE_FONT_SIZE = 12; // Code blocks
const LIST_FONT_SIZE = 14; // List items

// ============================================================================
// CONTENT LIMITS PER SLIDE
// To ensure readable, well-spaced presentations
// ============================================================================
const MAX_LIST_ITEMS_PER_SLIDE = 5; // Max bullet/numbered items before new slide
const MAX_PARAGRAPHS_PER_SLIDE = 2; // Max paragraphs before new slide
const MAX_TABLE_ROWS_PER_SLIDE = 6; // Max table rows before splitting (reduced)

// ============================================================================
// ARTEFACT TITLE SLIDE STYLING
// Centred, prominent title for the opening slide of each artefact
// ============================================================================
const ARTEFACT_TITLE_SLIDE = {
  x: 0.5,
  y: 2.5,
  w: 9,
  h: 1.5,
  fontSize: 40,
  color: "363636",
} as const;

/**
 * Estimate the height needed for text based on character count and available width
 */
function estimateTextHeight(text: string, fontSize: number = BODY_FONT_SIZE): number {
  // Adjust characters per line based on font size (baseline is BODY_FONT_SIZE)
  const charsPerLine = Math.floor(CHARS_PER_LINE * (BODY_FONT_SIZE / fontSize));
  const lineCount = Math.ceil(text.length / charsPerLine);
  // Adjust line height based on font size
  const adjustedLineHeight = LINE_HEIGHT * (fontSize / BODY_FONT_SIZE);

  return Math.max(adjustedLineHeight, lineCount * adjustedLineHeight);
}

/**
 * Internal interface for text formatting detection
 */
interface TextPart {
  text: string;
  options: {
    bold?: boolean;
    italic?: boolean;
    hyperlink?: string;
  };
}

/**
 * Converts Markdown to PowerPoint slides
 *
 * Creates slides with proper formatting including:
 * - All heading levels (H1-H6) create new slides
 * - Ordered and unordered lists with proper numbering/bullets
 * - Bold, italic, strikethrough formatting
 * - Clickable hyperlinks
 * - Code blocks with monospace font
 * - Tables
 * - Blockquotes
 *
 * @param markdown - The markdown content to convert
 * @param options - Optional metadata for the presentation
 * @returns A PptxGenJS presentation instance ready to be saved
 */
export async function convertMarkdownToPptxSlides(
  markdown: string,
  options: PptxConversionOptions = {},
): Promise<PptxGenJS> {
  const pptx = new PptxGenJS();

  // Set presentation metadata
  if (options.author) pptx.author = options.author;
  if (options.subject) pptx.subject = options.subject;
  if (options.title) pptx.title = options.title;

  pptx.layout = "LAYOUT_16x9";

  // Extract font options with defaults
  const fontFamily = options.fontFamily; // undefined = PowerPoint default
  const codeFontFamily = options.codeFontFamily || "Courier New";

  // Convert markdown to HTML using marked
  const html = await marked(markdown, {
    gfm: true,
    breaks: false,
  });

  // Parse HTML into DOM
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Title slide if provided
  if (options.title) {
    const titleSlide = pptx.addSlide();

    titleSlide.addText(options.title, {
      ...ARTEFACT_TITLE_SLIDE,
      bold: true,
      align: "center",
      ...(fontFamily && { fontFace: fontFamily }),
    });
  }

  let currentSlide: any = null;
  let currentY = CONTENT_START_Y;
  let currentSlideTitle = ""; // Track current slide title for continuation slides
  let paragraphsOnCurrentSlide = 0; // Track paragraphs for better distribution
  let listItemsOnCurrentSlide = 0; // Track list items for better distribution

  /**
   * Helper to create a new content slide (continuation)
   */
  const createContinuationSlide = (title: string, resetCounters = true) => {
    currentSlide = pptx.addSlide();
    const displayTitle = title ? `${title} (continued)` : "Content (continued)";

    // Add title at top of slide
    currentSlide.addText(displayTitle, {
      x: SLIDE_MARGIN_X,
      y: SLIDE_MARGIN_TOP,
      w: CONTENT_WIDTH,
      h: TITLE_HEIGHT,
      fontSize: HEADING_FONT_SIZE,
      bold: true,
      color: "363636",
      valign: "top", // Ensure title aligns to top of its container
      ...(fontFamily && { fontFace: fontFamily }),
    });
    // Content starts below the title area
    currentY = CONTENT_START_Y;

    if (resetCounters) {
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;
    }
  };

  /**
   * Helper to check if we need a new slide and create one if necessary
   * Considers both vertical space AND content density limits
   */
  const ensureSpaceForContent = (requiredHeight: number) => {
    if (currentY + requiredHeight > MAX_CONTENT_Y) {
      createContinuationSlide(currentSlideTitle);
    }
  };

  /**
   * Helper to check if we've hit paragraph limit and need a new slide
   */
  const checkParagraphLimit = () => {
    if (paragraphsOnCurrentSlide >= MAX_PARAGRAPHS_PER_SLIDE) {
      createContinuationSlide(currentSlideTitle);
    }
  };

  /**
   * Helper to check if we've hit list item limit and need a new slide
   */
  const checkListItemLimit = () => {
    if (listItemsOnCurrentSlide >= MAX_LIST_ITEMS_PER_SLIDE) {
      createContinuationSlide(currentSlideTitle);
      listItemsOnCurrentSlide = 0;
    }
  };

  // Process each child node in the body
  for (const node of Array.from(doc.body.childNodes)) {
    if (!node || node.nodeType !== window.Node.ELEMENT_NODE) continue;

    const element = node as HTMLElement;
    const nodeName = element.nodeName.toLowerCase();

    // Headings create new slides
    if (nodeName.match(/^h[1-6]$/)) {
      const level = parseInt(nodeName[1]);
      const text = element.textContent || "";
      const fontSize =
        level === 1 ? TITLE_FONT_SIZE : level === 2 ? SUBTITLE_FONT_SIZE : HEADING_FONT_SIZE;

      currentSlide = pptx.addSlide();
      currentSlideTitle = text;
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;

      // Add title at top of slide
      currentSlide.addText(text, {
        x: SLIDE_MARGIN_X,
        y: SLIDE_MARGIN_TOP,
        w: CONTENT_WIDTH,
        h: TITLE_HEIGHT,
        fontSize: fontSize,
        bold: true,
        color: "363636",
        valign: "top", // Ensure title aligns to top of its container
        ...(fontFamily && { fontFace: fontFamily }),
      });
      // Content starts below the title area
      currentY = CONTENT_START_Y;
      continue;
    }

    // Ensure we have a slide for content
    if (!currentSlide) {
      currentSlide = pptx.addSlide();
      currentSlideTitle = "Content";
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;

      currentSlide.addText("Content", {
        x: SLIDE_MARGIN_X,
        y: SLIDE_MARGIN_TOP,
        w: CONTENT_WIDTH,
        h: TITLE_HEIGHT,
        fontSize: SUBTITLE_FONT_SIZE,
        bold: true,
        color: "363636",
        ...(fontFamily && { fontFace: fontFamily }),
      });
      currentY = CONTENT_START_Y;
    }

    // Handle different content types
    switch (nodeName) {
      case "p": {
        const text = element.textContent || "";

        if (text.trim()) {
          // Check both space and paragraph limit
          checkParagraphLimit();
          const textHeight = estimateTextHeight(text, BODY_FONT_SIZE);

          ensureSpaceForContent(textHeight);
          currentSlide.addText(text, {
            x: SLIDE_MARGIN_X,
            y: currentY,
            w: CONTENT_WIDTH,
            fontSize: BODY_FONT_SIZE,
            color: "363636",
            valign: "top", // Ensure text starts at top of position
            ...(fontFamily && { fontFace: fontFamily }),
          });
          currentY += textHeight + PARAGRAPH_SPACING; // Add spacing after paragraph
          paragraphsOnCurrentSlide++;
        }
        break;
      }

      case "ul": {
        const result = processListItems(
          currentSlide,
          element,
          currentY,
          false,
          0,
          fontFamily,
          pptx,
          currentSlideTitle,
          ensureSpaceForContent,
          () => {
            createContinuationSlide(currentSlideTitle);
            listItemsOnCurrentSlide = 0;

            return currentSlide;
          },
          listItemsOnCurrentSlide,
          checkListItemLimit,
        );

        currentY = result.currentY;
        listItemsOnCurrentSlide = result.itemCount;
        currentSlide = result.currentSlide;
        break;
      }

      case "ol": {
        const result = processListItems(
          currentSlide,
          element,
          currentY,
          true,
          0,
          fontFamily,
          pptx,
          currentSlideTitle,
          ensureSpaceForContent,
          () => {
            createContinuationSlide(currentSlideTitle);
            listItemsOnCurrentSlide = 0;

            return currentSlide;
          },
          listItemsOnCurrentSlide,
          checkListItemLimit,
        );

        currentY = result.currentY;
        listItemsOnCurrentSlide = result.itemCount;
        currentSlide = result.currentSlide;
        break;
      }

      case "pre": {
        const code = element.querySelector("code") || element;
        const text = code.textContent || "";
        const codeHeight = estimateTextHeight(text, CODE_FONT_SIZE);

        ensureSpaceForContent(codeHeight);
        currentSlide.addText(text, {
          x: SLIDE_MARGIN_X,
          y: currentY,
          w: CONTENT_WIDTH,
          fontSize: CODE_FONT_SIZE,
          fontFace: codeFontFamily,
          color: "000000",
          fill: { color: "F5F5F5" },
        });
        currentY += codeHeight + 0.25;
        break;
      }

      case "table": {
        const tableResult = processTable(currentSlide, element, currentY, fontFamily, () => {
          createContinuationSlide(currentSlideTitle);

          return currentSlide;
        });

        currentY = tableResult.currentY;
        currentSlide = tableResult.currentSlide;
        break;
      }

      case "blockquote": {
        for (const child of Array.from(element.children)) {
          if (child.tagName === "P") {
            const text = child.textContent || "";
            const textHeight = estimateTextHeight(text, BODY_FONT_SIZE);

            ensureSpaceForContent(textHeight);
            currentSlide.addText(text, {
              x: SLIDE_MARGIN_X + 0.5, // Indent blockquotes
              y: currentY,
              w: CONTENT_WIDTH - 0.5,
              fontSize: BODY_FONT_SIZE,
              italic: true,
              color: "555555",
              ...(fontFamily && { fontFace: fontFamily }),
            });
            currentY += textHeight + 0.2;
          }
        }
        break;
      }

      case "hr": {
        ensureSpaceForContent(0.3);
        currentSlide.addShape("rect", {
          x: SLIDE_MARGIN_X,
          y: currentY,
          w: CONTENT_WIDTH,
          h: 0.02,
          fill: { color: "CCCCCC" },
        });
        currentY += 0.3;
        break;
      }
    }
  }

  return pptx;
}

/**
 * Convert TextPart array to pptxgenjs text objects
 * Preserves all formatting including hyperlinks
 */
function convertTextPartsToTextObjects(
  parts: TextPart[],
  fontSize: number,
  fontFamily?: string,
): any[] {
  return parts.map((part) => {
    const options: any = {
      fontSize: fontSize,
      color: "363636",
    };

    if (fontFamily) options.fontFace = fontFamily;
    if (part.options.bold) options.bold = true;
    if (part.options.italic) options.italic = true;
    if (part.options.hyperlink) {
      options.hyperlink = { url: part.options.hyperlink };
    }

    return { text: part.text, options };
  });
}

/**
 * Extract text parts with formatting for bold detection
 * Only used to detect the **Term:** Description pattern
 */
function extractTextParts(element: HTMLElement | globalThis.Node): TextPart[] {
  const parts: TextPart[] = [];

  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === window.Node.TEXT_NODE) {
      const text = child.textContent || "";

      if (text.trim()) {
        parts.push({
          text: text,
          options: {},
        });
      }
    } else if (child.nodeType === window.Node.ELEMENT_NODE) {
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === "strong" || tag === "b") {
        const text = el.textContent || "";

        if (text.trim()) {
          parts.push({
            text: text,
            options: { bold: true },
          });
        }
      } else if (tag === "em" || tag === "i") {
        const text = el.textContent || "";

        if (text.trim()) {
          parts.push({
            text: text,
            options: { italic: true },
          });
        }
      } else if (tag === "a") {
        const text = el.textContent || "";
        const href = el.getAttribute("href") || "";

        if (text.trim()) {
          parts.push({
            text: text,
            options: { hyperlink: href },
          });
        }
      } else {
        parts.push(...extractTextParts(el));
      }
    }
  }

  return parts;
}

/**
 * Return type for processListItems
 */
interface ListProcessingResult {
  currentY: number;
  itemCount: number;
  currentSlide: any;
}

/**
 * Process list items (ordered or unordered)
 * Returns both the Y position and item count for tracking
 */
function processListItems(
  slide: any,
  listElement: HTMLElement,
  startY: number,
  ordered: boolean,
  level: number,
  fontFamily?: string,
  _pptx?: any,
  _slideTitle?: string,
  ensureSpace?: (height: number) => void,
  createNewSlide?: () => any,
  initialItemCount: number = 0,
  checkItemLimit?: () => void,
): ListProcessingResult {
  let currentY = startY;
  let itemNumber = 1;
  let currentSlide = slide;
  let itemsProcessed = initialItemCount;

  for (const child of Array.from(listElement.children)) {
    if (child.nodeName === "LI") {
      const li = child as HTMLElement;

      // Separate nested lists from text content
      const nestedLists: HTMLElement[] = [];
      const contentNodes: Node[] = [];

      for (const node of Array.from(li.childNodes)) {
        if (node.nodeType === window.Node.ELEMENT_NODE) {
          const el = node as HTMLElement;

          if (el.tagName === "UL" || el.tagName === "OL") {
            nestedLists.push(el);
            continue;
          }
        }
        contentNodes.push(node);
      }

      // Process text content
      if (contentNodes.length > 0) {
        const textParts: TextPart[] = [];

        for (const node of contentNodes) {
          // Use extractTextParts for both text and element nodes to preserve formatting
          textParts.push(...extractTextParts(node));
        }

        if (textParts.length > 0) {
          const indent = SLIDE_MARGIN_X + level * 0.4;
          const contentW = CONTENT_WIDTH - level * 0.4;
          const fullText = textParts.map((p) => p.text).join("");
          const itemHeight = estimateTextHeight(fullText, LIST_FONT_SIZE);

          // Check item limit first (only at level 0 for main list items)
          if (level === 0 && checkItemLimit) {
            if (itemsProcessed >= MAX_LIST_ITEMS_PER_SLIDE) {
              currentSlide = createNewSlide ? createNewSlide() : currentSlide;
              currentY = CONTENT_START_Y;
              itemsProcessed = 0;
            }
          }

          // Check if we need a new slide for this item due to space
          if (ensureSpace && createNewSlide && currentY + itemHeight > MAX_CONTENT_Y) {
            currentSlide = createNewSlide();
            currentY = CONTENT_START_Y;
            itemsProcessed = 0;
          }

          // Check if we have bold text at the start followed by description (common pattern: **Term:** Description)
          const hasBoldTerm =
            textParts.length > 1 &&
            textParts[0].options.bold &&
            textParts[0].text.trim().endsWith(":") &&
            !textParts[1].options.bold;

          if (hasBoldTerm) {
            // WORKAROUND: Use line break formatting for better bold support
            // First line: bullet + bold term
            const boldTerm = textParts[0].text.trim();
            // Remaining text (skip the colon separator and regular text)
            const descriptionParts = textParts.slice(1);
            const descText = descriptionParts.map((p) => p.text).join("");
            const descHeight = estimateTextHeight(descText, LIST_FONT_SIZE);

            if (ordered) {
              // Ordered list with bold term on first line
              currentSlide.addText(`${itemNumber}. ${boldTerm}`, {
                x: indent,
                y: currentY,
                w: contentW,
                fontSize: LIST_FONT_SIZE,
                bold: true,
                color: "363636",
                ...(fontFamily && { fontFace: fontFamily }),
              });
              currentY += 0.4;

              // Description on second line, indented more (with hyperlinks if present)
              if (descriptionParts.length > 0) {
                const descriptionTextObjects = convertTextPartsToTextObjects(
                  descriptionParts,
                  LIST_FONT_SIZE,
                  fontFamily,
                );

                currentSlide.addText(descriptionTextObjects, {
                  x: indent + 0.3,
                  y: currentY,
                  w: contentW - 0.3,
                  fontSize: LIST_FONT_SIZE,
                  color: "363636",
                  ...(fontFamily && { fontFace: fontFamily }),
                });
                currentY += descHeight + 0.2;
              } else {
                currentY += 0.2;
              }
            } else {
              // Unordered list with bold term on first line
              currentSlide.addText(boldTerm, {
                x: indent,
                y: currentY,
                w: contentW,
                fontSize: LIST_FONT_SIZE,
                bold: true,
                color: "363636",
                bullet: { type: "bullet", indent: level * 18 },
                ...(fontFamily && { fontFace: fontFamily }),
              });
              currentY += 0.4;

              // Description on second line, indented more (with hyperlinks if present)
              if (descriptionParts.length > 0) {
                const descriptionTextObjects = convertTextPartsToTextObjects(
                  descriptionParts,
                  LIST_FONT_SIZE,
                  fontFamily,
                );

                currentSlide.addText(descriptionTextObjects, {
                  x: indent + 0.3,
                  y: currentY,
                  w: contentW - 0.3,
                  fontSize: LIST_FONT_SIZE,
                  color: "363636",
                  ...(fontFamily && { fontFace: fontFamily }),
                });
                currentY += descHeight + 0.2;
              } else {
                currentY += 0.2;
              }
            }

            itemNumber++;
            itemsProcessed++;
          } else {
            // Standard formatting for lists without bold terms
            // Convert text parts to objects that support hyperlinks
            const textObjects = convertTextPartsToTextObjects(
              textParts,
              LIST_FONT_SIZE,
              fontFamily,
            );

            if (ordered) {
              // Prepend the number to the first text object
              textObjects.unshift({
                text: `${itemNumber}. `,
                options: {
                  fontSize: LIST_FONT_SIZE,
                  color: "363636",
                  ...(fontFamily && { fontFace: fontFamily }),
                },
              });

              currentSlide.addText(textObjects, {
                x: indent,
                y: currentY,
                w: contentW,
                fontSize: LIST_FONT_SIZE,
                color: "363636",
                ...(fontFamily && { fontFace: fontFamily }),
              });
            } else {
              // Plain text - use pptxgenjs bullet
              currentSlide.addText(textObjects, {
                x: indent,
                y: currentY,
                w: contentW,
                fontSize: LIST_FONT_SIZE,
                color: "363636",
                bullet: { type: "bullet", indent: level * 18 },
                ...(fontFamily && { fontFace: fontFamily }),
              });
            }

            currentY += itemHeight + 0.2;
            itemNumber++;
            itemsProcessed++;
          }
        }
      }

      // Process nested lists
      for (const nestedList of nestedLists) {
        const isOrdered = nestedList.tagName === "OL";

        const nestedResult = processListItems(
          currentSlide,
          nestedList,
          currentY,
          isOrdered,
          level + 1,
          fontFamily,
          _pptx,
          _slideTitle,
          ensureSpace,
          createNewSlide,
          itemsProcessed,
          checkItemLimit,
        );

        currentY = nestedResult.currentY;
        // Don't count nested items towards main list limit
      }
    }
  }

  return { currentY, itemCount: itemsProcessed, currentSlide };
}

// Table font sizes for presentation display
const TABLE_HEADER_FONT_SIZE = 14;
const TABLE_BODY_FONT_SIZE = 12;
const TABLE_ROW_HEIGHT = 0.35; // Height per row in inches

/**
 * Return type for processTable
 */
interface TableProcessingResult {
  currentY: number;
  currentSlide: any;
}

/**
 * Process table into PowerPoint table with pagination support
 * Tables with many rows are split across multiple slides
 */
function processTable(
  slide: any,
  tableElement: HTMLElement,
  startY: number,
  fontFamily?: string,
  createNewSlide?: () => any,
): TableProcessingResult {
  // Extract all rows with header info
  const headerRows: any[][] = [];
  const bodyRows: any[][] = [];

  // Extract table data
  for (const section of Array.from(tableElement.children)) {
    if (section.tagName === "THEAD" || section.tagName === "TBODY") {
      const isHeader = section.tagName === "THEAD";

      for (const row of Array.from(section.children)) {
        if (row.tagName === "TR") {
          const rowData: any[] = [];

          for (const cell of Array.from(row.children)) {
            if (cell.tagName === "TD" || cell.tagName === "TH") {
              rowData.push({
                text: cell.textContent || "",
                options: {
                  fontSize: isHeader ? TABLE_HEADER_FONT_SIZE : TABLE_BODY_FONT_SIZE,
                  bold: isHeader,
                  fill: isHeader ? "E8E8E8" : "FFFFFF",
                  color: "363636",
                  valign: "middle",
                  ...(fontFamily && { fontFace: fontFamily }),
                },
              });
            }
          }
          if (rowData.length > 0) {
            if (isHeader) {
              headerRows.push(rowData);
            } else {
              bodyRows.push(rowData);
            }
          }
        }
      }
    }
  }

  if (headerRows.length === 0 && bodyRows.length === 0) {
    return { currentY: startY, currentSlide: slide };
  }

  let currentSlide = slide;
  let currentY = startY;

  // Calculate how many rows fit on the current slide
  const availableHeight = MAX_CONTENT_Y - currentY;
  const headerHeight = headerRows.length * TABLE_ROW_HEIGHT;
  const rowsPerSlide = Math.floor((availableHeight - headerHeight) / TABLE_ROW_HEIGHT);

  // If we can fit the whole table, just add it
  if (bodyRows.length <= rowsPerSlide && bodyRows.length <= MAX_TABLE_ROWS_PER_SLIDE) {
    const allRows = [...headerRows, ...bodyRows];

    currentSlide.addTable(allRows, {
      x: SLIDE_MARGIN_X,
      y: currentY,
      w: CONTENT_WIDTH,
      fontSize: TABLE_BODY_FONT_SIZE,
      border: { pt: 1, color: "CCCCCC" },
      rowH: TABLE_ROW_HEIGHT,
      ...(fontFamily && { fontFace: fontFamily }),
    });

    return {
      currentY: currentY + allRows.length * TABLE_ROW_HEIGHT + 0.3,
      currentSlide,
    };
  }

  // Split table across multiple slides
  let rowIndex = 0;
  let isFirstPart = true;

  while (rowIndex < bodyRows.length) {
    // Calculate rows for this slide
    const maxRowsThisSlide = isFirstPart
      ? Math.min(rowsPerSlide, MAX_TABLE_ROWS_PER_SLIDE)
      : MAX_TABLE_ROWS_PER_SLIDE;

    const rowsForThisSlide = bodyRows.slice(rowIndex, rowIndex + maxRowsThisSlide);
    const tableRows = [...headerRows, ...rowsForThisSlide];

    // Add table to current slide
    currentSlide.addTable(tableRows, {
      x: SLIDE_MARGIN_X,
      y: currentY,
      w: CONTENT_WIDTH,
      fontSize: TABLE_BODY_FONT_SIZE,
      border: { pt: 1, color: "CCCCCC" },
      rowH: TABLE_ROW_HEIGHT,
      ...(fontFamily && { fontFace: fontFamily }),
    });

    rowIndex += rowsForThisSlide.length;
    currentY = currentY + tableRows.length * TABLE_ROW_HEIGHT + 0.3;

    // If there are more rows, create a new slide
    if (rowIndex < bodyRows.length && createNewSlide) {
      currentSlide = createNewSlide();
      currentY = CONTENT_START_Y;
      isFirstPart = false;
    }
  }

  return { currentY, currentSlide };
}

/**
 * Adds markdown content as slides to an existing PowerPoint presentation
 *
 * This function is designed for batch export scenarios where you want to add
 * multiple artefacts to a single presentation. It uses the same sophisticated
 * multi-slide logic as convertMarkdownToPptxSlides, properly spreading content
 * across multiple slides when needed.
 *
 * @param pptx - The existing PptxGenJS presentation to add slides to
 * @param markdown - The markdown content to convert to slides
 * @param options - Options for slide generation
 */
export async function addMarkdownSlidesToPresentation(
  pptx: PptxGenJS,
  markdown: string,
  options: AddSlidesToPresentationOptions = {},
): Promise<void> {
  // Extract font options with defaults
  const fontFamily = options.fontFamily; // undefined = PowerPoint default
  const codeFontFamily = options.codeFontFamily || "Courier New";

  // Convert markdown to HTML using marked
  const html = await marked(markdown, {
    gfm: true,
    breaks: false,
  });

  // Parse HTML into DOM
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Title slide for this artefact if provided and not skipped
  // Uses shared ARTEFACT_TITLE_SLIDE constants for consistent styling
  if (options.title && !options.skipTitleSlide) {
    const titleSlide = pptx.addSlide();

    titleSlide.addText(options.title, {
      ...ARTEFACT_TITLE_SLIDE,
      bold: true,
      align: "center",
      ...(fontFamily && { fontFace: fontFamily }),
    });
  }

  let currentSlide: any = null;
  let currentY = CONTENT_START_Y;
  let currentSlideTitle = options.title || "Content"; // Track current slide title for continuation slides
  let paragraphsOnCurrentSlide = 0; // Track paragraphs for better distribution
  let listItemsOnCurrentSlide = 0; // Track list items for better distribution

  /**
   * Helper to create a new content slide (continuation)
   */
  const createContinuationSlide = (title: string, resetCounters = true) => {
    currentSlide = pptx.addSlide();
    const displayTitle = title ? `${title} (continued)` : "Content (continued)";

    // Add title at top of slide
    currentSlide.addText(displayTitle, {
      x: SLIDE_MARGIN_X,
      y: SLIDE_MARGIN_TOP,
      w: CONTENT_WIDTH,
      h: TITLE_HEIGHT,
      fontSize: HEADING_FONT_SIZE,
      bold: true,
      color: "363636",
      valign: "top",
      ...(fontFamily && { fontFace: fontFamily }),
    });
    // Content starts below the title area
    currentY = CONTENT_START_Y;

    if (resetCounters) {
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;
    }
  };

  /**
   * Helper to check if we need a new slide and create one if necessary
   */
  const ensureSpaceForContent = (requiredHeight: number) => {
    if (currentY + requiredHeight > MAX_CONTENT_Y) {
      createContinuationSlide(currentSlideTitle);
    }
  };

  /**
   * Helper to check if we've hit paragraph limit and need a new slide
   */
  const checkParagraphLimit = () => {
    if (paragraphsOnCurrentSlide >= MAX_PARAGRAPHS_PER_SLIDE) {
      createContinuationSlide(currentSlideTitle);
    }
  };

  /**
   * Helper to check if we've hit list item limit and need a new slide
   */
  const checkListItemLimit = () => {
    if (listItemsOnCurrentSlide >= MAX_LIST_ITEMS_PER_SLIDE) {
      createContinuationSlide(currentSlideTitle);
      listItemsOnCurrentSlide = 0;
    }
  };

  // Process each child node in the body
  for (const node of Array.from(doc.body.childNodes)) {
    if (!node || node.nodeType !== window.Node.ELEMENT_NODE) continue;

    const element = node as HTMLElement;
    const nodeName = element.nodeName.toLowerCase();

    // Headings create new slides
    if (nodeName.match(/^h[1-6]$/)) {
      const level = parseInt(nodeName[1]);
      const text = element.textContent || "";
      const fontSize =
        level === 1 ? TITLE_FONT_SIZE : level === 2 ? SUBTITLE_FONT_SIZE : HEADING_FONT_SIZE;

      currentSlide = pptx.addSlide();
      currentSlideTitle = text;
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;

      // Add title at top of slide
      currentSlide.addText(text, {
        x: SLIDE_MARGIN_X,
        y: SLIDE_MARGIN_TOP,
        w: CONTENT_WIDTH,
        h: TITLE_HEIGHT,
        fontSize: fontSize,
        bold: true,
        color: "363636",
        valign: "top",
        ...(fontFamily && { fontFace: fontFamily }),
      });
      // Content starts below the title area
      currentY = CONTENT_START_Y;
      continue;
    }

    // Ensure we have a slide for content
    if (!currentSlide) {
      currentSlide = pptx.addSlide();
      paragraphsOnCurrentSlide = 0;
      listItemsOnCurrentSlide = 0;

      currentSlide.addText(currentSlideTitle, {
        x: SLIDE_MARGIN_X,
        y: SLIDE_MARGIN_TOP,
        w: CONTENT_WIDTH,
        h: TITLE_HEIGHT,
        fontSize: SUBTITLE_FONT_SIZE,
        bold: true,
        color: "363636",
        ...(fontFamily && { fontFace: fontFamily }),
      });
      currentY = CONTENT_START_Y;
    }

    // Handle different content types
    switch (nodeName) {
      case "p": {
        const text = element.textContent || "";

        if (text.trim()) {
          // Check both space and paragraph limit
          checkParagraphLimit();
          const textHeight = estimateTextHeight(text, BODY_FONT_SIZE);

          ensureSpaceForContent(textHeight);
          currentSlide.addText(text, {
            x: SLIDE_MARGIN_X,
            y: currentY,
            w: CONTENT_WIDTH,
            fontSize: BODY_FONT_SIZE,
            color: "363636",
            valign: "top",
            ...(fontFamily && { fontFace: fontFamily }),
          });
          currentY += textHeight + PARAGRAPH_SPACING;
          paragraphsOnCurrentSlide++;
        }
        break;
      }

      case "ul": {
        checkListItemLimit();
        const result = processListItems(
          currentSlide,
          element,
          currentY,
          false,
          0,
          fontFamily,
          pptx,
          currentSlideTitle,
          () => {
            createContinuationSlide(currentSlideTitle);

            return currentSlide;
          },
        );

        currentY = result.currentY;
        currentSlide = result.currentSlide;
        listItemsOnCurrentSlide += result.itemCount;
        break;
      }

      case "ol": {
        checkListItemLimit();
        const result = processListItems(
          currentSlide,
          element,
          currentY,
          true,
          0,
          fontFamily,
          pptx,
          currentSlideTitle,
          () => {
            createContinuationSlide(currentSlideTitle);

            return currentSlide;
          },
        );

        currentY = result.currentY;
        currentSlide = result.currentSlide;
        listItemsOnCurrentSlide += result.itemCount;
        break;
      }

      case "pre": {
        // Code block
        const codeElement = element.querySelector("code");
        const code = codeElement?.textContent || element.textContent || "";
        const codeHeight = estimateTextHeight(code, CODE_FONT_SIZE) + 0.5;

        ensureSpaceForContent(codeHeight);
        currentSlide.addText(code, {
          x: SLIDE_MARGIN_X,
          y: currentY,
          w: CONTENT_WIDTH,
          fontSize: CODE_FONT_SIZE,
          fontFace: codeFontFamily,
          color: "2D3748",
          fill: { color: "F7FAFC" },
          valign: "top",
        });
        currentY += codeHeight;
        break;
      }

      case "blockquote": {
        const quoteText = element.textContent || "";
        const quoteHeight = estimateTextHeight(quoteText, BODY_FONT_SIZE) + 0.3;

        ensureSpaceForContent(quoteHeight);
        currentSlide.addText(quoteText, {
          x: SLIDE_MARGIN_X + 0.3,
          y: currentY,
          w: CONTENT_WIDTH - 0.3,
          fontSize: BODY_FONT_SIZE,
          italic: true,
          color: "666666",
          valign: "top",
          ...(fontFamily && { fontFace: fontFamily }),
        });
        currentY += quoteHeight;
        break;
      }

      case "table": {
        const tableResult = processTable(currentSlide, element, currentY, fontFamily, () => {
          createContinuationSlide(currentSlideTitle);

          return currentSlide;
        });

        currentY = tableResult.currentY;
        currentSlide = tableResult.currentSlide;
        break;
      }

      case "hr": {
        // Horizontal rule - add spacing
        currentY += 0.3;
        break;
      }

      default:
        // For other elements, try to extract text content
        if (element.textContent?.trim()) {
          const text = element.textContent;
          const textHeight = estimateTextHeight(text, BODY_FONT_SIZE);

          ensureSpaceForContent(textHeight);
          currentSlide.addText(text, {
            x: SLIDE_MARGIN_X,
            y: currentY,
            w: CONTENT_WIDTH,
            fontSize: BODY_FONT_SIZE,
            color: "363636",
            valign: "top",
            ...(fontFamily && { fontFace: fontFamily }),
          });
          currentY += textHeight + PARAGRAPH_SPACING;
        }
    }
  }
}
